import React, { useEffect } from "react";

import { Box } from "@chakra-ui/react";
import { useBusinesses } from "contexts/businesses-context";
import {
  IGetBusinessAccountsPayload,
  TAccountStatus,
  TBusinessAccountType,
} from "contexts/businesses-context/types";
import { useParams, useSearchParams } from "react-router-dom";
import { handleAmountFormat, handleTruncateAmount } from "utils";
import { ONE_NAIRA_IN_KOBO, ZERO } from "utils/constants";

import { Spinner } from "ui/spinner";
import { Heading3, Paragraph3 } from "ui/typography";

import { BusinessAccountsTable } from "./BusinessAccountsTable";

export const BusinessAccounts = () => {
  const { businessId } = useParams();
  const [searchParams] = useSearchParams();
  const {
    isLoadingBusinessAccounts,
    handleGetBusinessAccounts,
    businessAccountsData,
  } = useBusinesses();
  const walletType = searchParams.get("walletType");
  const startDate = searchParams.get("startDate");
  const endDate = searchParams.get("endDate");
  const accountStatus = searchParams.get("accountStatus");
  const status = searchParams.get("status");
  const filterRetryCount = searchParams.get("filterRetryCount");

  useEffect(() => {
    if (!businessId) return;
    const payload: IGetBusinessAccountsPayload = {
      businessId,
    };

    if (startDate) {
      payload["startDate"] = startDate;
    }

    if (endDate) {
      payload["endDate"] = endDate;
    }

    if (walletType) {
      const walletTypes = walletType.split(",");
      const containsExpense = walletTypes.includes("expense");
      const containsCollection = walletTypes.includes("collection");
      if (
        !walletTypes.includes("all") &&
        containsExpense !== containsCollection
      ) {
        const [walletTypeToSend] = walletTypes;
        payload["walletType"] = walletTypeToSend as TBusinessAccountType;
      }
    }

    if (accountStatus) {
      payload["accountStatus"] = accountStatus as TAccountStatus;
    }

    if (status) {
      const accountStatuses = status.split(",");
      const containsActive = accountStatuses.includes("active");
      const containsInactive = accountStatuses.includes("inactive");
      if (
        !accountStatuses.includes("all") &&
        containsActive !== containsInactive
      ) {
        const [statusToSend] = accountStatuses;
        payload["status"] = statusToSend as TAccountStatus;
      } else if (
        accountStatuses.includes("all") ||
        (accountStatus && containsActive && containsInactive)
      ) {
        delete payload["accountStatus"];
      }
    }

    void handleGetBusinessAccounts(payload);
  }, [
    businessId,
    walletType,
    startDate,
    endDate,
    accountStatus,
    status,
    filterRetryCount,
  ]);

  return (
    <Box className="flex flex-col gap-2 mt-1">
      <Box
        className="bg-white pt-5 px-6 pb-4 rounded-[20px]"
        css={{ boxShadow: "0px 0px 5px 0px #0000000D" }}
      >
        <Box className="flex flex-col gap-1">
          <Paragraph3
            lineHeight="14px"
            textColor="#718096"
          >
            Total Account Balance
          </Paragraph3>
          {isLoadingBusinessAccounts ? (
            <Spinner size="24px" />
          ) : (
            <Heading3
              lineHeight="20px"
              fontWeight="700"
              textColor="#1A202C"
            >
              {handleAmountFormat({
                value: (
                  handleTruncateAmount(
                    businessAccountsData.totalBalance || ZERO
                  ) / ONE_NAIRA_IN_KOBO
                ).toString(),
                includeNairaSymbol: true,
                isFormattedAmtNotPadded: true,
                shouldFormatZero: true,
              })}
            </Heading3>
          )}
        </Box>
      </Box>
      <Box
        className="bg-white pt-5 px-6 pb-4 rounded-[20px]"
        css={{ boxShadow: "0px 0px 5px 0px #0000000D" }}
      >
        <BusinessAccountsTable
          businessAccounts={businessAccountsData.businessAccount}
          isLoading={isLoadingBusinessAccounts}
          handleGetBusinessAccounts={handleGetBusinessAccounts}
        />
      </Box>
    </Box>
  );
};
